import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, MessageSquare, Scissors, TrendingUp, Clock, CheckCircle } from 'lucide-react';
import { getBookings, getContacts, getServices } from '../../services/api';

const Dashboard = () => {
  const [stats, setStats] = useState({
    totalBookings: 0,
    pendingBookings: 0,
    confirmedBookings: 0,
    unreadMessages: 0,
    totalServices: 0
  });
  const [recentBookings, setRecentBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [bookings, contacts, services] = await Promise.all([
          getBookings(),
          getContacts(),
          getServices(null, false)
        ]);

        setStats({
          totalBookings: bookings.length,
          pendingBookings: bookings.filter(b => b.status === 'pending').length,
          confirmedBookings: bookings.filter(b => b.status === 'confirmed').length,
          unreadMessages: contacts.filter(c => !c.is_read).length,
          totalServices: services.length
        });

        setRecentBookings(bookings.slice(0, 5));
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const statCards = [
    { label: 'Total Bookings', value: stats.totalBookings, icon: Calendar, color: 'bg-[var(--accent-blush)]' },
    { label: 'Pending', value: stats.pendingBookings, icon: Clock, color: 'bg-[var(--accent-nude)]' },
    { label: 'Confirmed', value: stats.confirmedBookings, icon: CheckCircle, color: 'bg-green-100' },
    { label: 'Unread Messages', value: stats.unreadMessages, icon: MessageSquare, color: 'bg-blue-100' },
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      case 'completed': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-48 skeleton rounded-lg" />
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1,2,3,4].map(i => <div key={i} className="h-24 skeleton rounded-xl" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="heading-1 mb-1">Dashboard</h1>
        <p className="body-small text-[var(--text-secondary)]">Welcome back! Here's what's happening.</p>
      </div>

      {/* Stats Grid */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat, i) => (
          <div key={i} className="bg-white p-5 rounded-xl">
            <div className="flex items-center justify-between mb-3">
              <div className={`w-10 h-10 rounded-lg ${stat.color} flex items-center justify-center`}>
                <stat.icon size={20} className="text-[var(--text-heading)]" />
              </div>
            </div>
            <p className="text-2xl font-medium text-[var(--text-heading)]" style={{fontFamily: "'Cormorant Garamond', serif"}}>{stat.value}</p>
            <p className="body-small text-[var(--text-secondary)]">{stat.label}</p>
          </div>
        ))}
      </div>

      {/* Recent Bookings */}
      <div className="bg-white rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="heading-3">Recent Bookings</h2>
          <Link to="/admin/appointments" className="text-sm text-[var(--text-secondary)] hover:text-[var(--text-heading)] transition-colors">
            View All
          </Link>
        </div>
        
        {recentBookings.length === 0 ? (
          <p className="text-center py-8 text-[var(--text-secondary)]">No bookings yet</p>
        ) : (
          <div className="space-y-3">
            {recentBookings.map((booking) => (
              <div key={booking.id} className="flex items-center justify-between p-3 bg-[var(--bg-subtle)] rounded-lg">
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-[var(--text-heading)] truncate">{booking.client_name}</p>
                  <p className="body-small text-[var(--text-secondary)]">{booking.service_name}</p>
                </div>
                <div className="text-right ml-4">
                  <p className="body-small text-[var(--text-heading)]">
                    {new Date(booking.booking_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </p>
                  <span className={`inline-block px-2 py-0.5 text-xs rounded-full capitalize ${getStatusColor(booking.status)}`}>
                    {booking.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Quick Links */}
      <div className="grid sm:grid-cols-3 gap-4">
        <Link to="/admin/appointments" className="bg-white p-5 rounded-xl hover:shadow-sm transition-shadow">
          <Calendar size={24} className="text-[var(--accent-blush-dark)] mb-3" />
          <h3 className="heading-3 mb-1">Appointments</h3>
          <p className="body-small text-[var(--text-secondary)]">Manage bookings</p>
        </Link>
        <Link to="/admin/services" className="bg-white p-5 rounded-xl hover:shadow-sm transition-shadow">
          <Scissors size={24} className="text-[var(--accent-nude-dark)] mb-3" />
          <h3 className="heading-3 mb-1">Services</h3>
          <p className="body-small text-[var(--text-secondary)]">Edit services & pricing</p>
        </Link>
        <Link to="/admin/messages" className="bg-white p-5 rounded-xl hover:shadow-sm transition-shadow">
          <MessageSquare size={24} className="text-blue-400 mb-3" />
          <h3 className="heading-3 mb-1">Messages</h3>
          <p className="body-small text-[var(--text-secondary)]">View inquiries</p>
        </Link>
      </div>
    </div>
  );
};

export default Dashboard;
